import { createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';
import {
  Container,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { faMemoPad } from '@fortawesome/pro-thin-svg-icons/faMemoPad';
import { myCampaignsDocument } from '@social-garden/api/documents/campaign.ts';
import { parseAsArrayOf, parseAsStringEnum, useQueryState } from 'nuqs';
import { CampaignStatus } from '@social-garden/api/gql/graphql.ts';
import BrandSelect from '@/components/BrandSelect.tsx';
import PageTitle from '@/components/PageTitle.tsx';
import GridList from '@/components/GridList.tsx';
import CampaignStatusSelect from '@/components/CampaignStatusSelect.tsx';
import SuspenseFallback from '@/components/SuspenseFallback.tsx';
import CampaignListItem, {
  CampaignListItemData,
} from '@/components/CampaignListItem.tsx';
import EmptyState from '@/components/EmptyState.tsx';
import useBrandIdsQueryState from '@/hooks/useBrandIdsQueryState.ts';
import ButtonLink from '@/components/ButtonLink.tsx';

export const Route = createFileRoute('/_manager/_app/campaigns/')({
  component: Campaigns,
});

function Campaigns() {
  const { t } = useTranslation(['common', 'manager']);
  const [brandIds, setBrandIds] = useBrandIdsQueryState();
  const [status, setStatus] = useQueryState(
    'campaign-status',
    parseAsArrayOf(
      parseAsStringEnum<CampaignStatus>(Object.values(CampaignStatus)),
    ).withDefault([]),
  );

  const { data, loading, fetchMore } = useQuery(myCampaignsDocument, {
    variables: {
      input: {
        brandIds,
        status,
      },
      first: 25,
      page: 1,
    },
  });

  const handleOnBrandChange = useCallback(
    async (event: SelectChangeEvent<string[]>) => {
      const selected = event.target.value;
      if (Array.isArray(selected)) {
        await setBrandIds(selected);
      }
    },
    [setBrandIds],
  );

  const handleOnStatusChange = useCallback(
    async (event: SelectChangeEvent<CampaignStatus[]>) => {
      const selected = event.target.value;
      if (Array.isArray(selected)) {
        await setStatus(selected);
      }
    },
    [setStatus],
  );

  const renderItem = useCallback(
    (campaignData: CampaignListItemData) => (
      <Grid
        key={campaignData.id}
        size={{
          xs: 12,
          sm: 12,
          md: 6,
          lg: 6,
          xl: 4,
        }}>
        <CampaignListItem variant="outlined" data={campaignData} />
      </Grid>
    ),
    [],
  );

  return (
    <Container>
      <Grid container spacing={4}>
        <Grid container size={12} alignItems="center">
          <Grid
            size={{
              xs: 12,
              md: 'grow',
            }}>
            <PageTitle>{t('manager:navigation.campaigns')}</PageTitle>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 'auto',
            }}>
            <ButtonLink variant="contained" size="large" to="/campaigns/create">
              {t('manager:campaign.create.newButton')}
            </ButtonLink>
          </Grid>
        </Grid>
        <Grid container size={12}>
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 6,
              lg: 6,
              xl: 4,
            }}>
            <FormControl fullWidth>
              <InputLabel id="filter-brand-label">
                {t('manager:campaign.filter.brand.label')}
              </InputLabel>
              <BrandSelect
                variant="outlined"
                labelId="filter-brand-label"
                label={t('manager:campaign.filter.brand.label')}
                value={brandIds}
                multiple
                onChange={handleOnBrandChange}
              />
            </FormControl>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 6,
              lg: 6,
              xl: 4,
            }}>
            <FormControl fullWidth>
              <InputLabel id="filter-status-label">
                {t('manager:campaign.filter.status.label')}
              </InputLabel>
              <CampaignStatusSelect
                labelId="filter-status-label"
                label={t('manager:campaign.filter.status.label')}
                value={status}
                multiple
                variant="outlined"
                onChange={handleOnStatusChange}
              />
            </FormControl>
          </Grid>
        </Grid>
        <Grid size={12}>
          {data ? (
            <GridList<CampaignListItemData>
              container
              spacing={4}
              paginator={data.myCampaigns}
              fetchMore={fetchMore}
              renderItem={renderItem}
              loading={loading}
              ListEmptyComponent={
                <Card variant="outlined">
                  <CardContent>
                    <EmptyState
                      icon={faMemoPad}
                      text={t('manager:campaign.campaignList.empty')}
                    />
                  </CardContent>
                </Card>
              }
            />
          ) : (
            <SuspenseFallback minHeight={300} />
          )}
        </Grid>
      </Grid>
    </Container>
  );
}
